import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Accordion, Form, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import { 
  FaDownload, 
  FaFilePdf, 
  FaBookOpen, 
  FaChevronDown, 
  FaChevronUp,
  FaCalendarAlt, 
  FaTools, 
  FaIdCard, 
  FaClipboardList,
  FaInfoCircle,
  FaBook,
  FaFileContract,
  FaWpforms,
  FaFolderOpen,
  FaFilter,
  FaEye,
  FaFileAlt,
  FaImage,
  FaExpand,
  FaCompress
} from 'react-icons/fa';
import supabase from '../../supabaseClient'; // Ajuste o caminho conforme sua estrutura
import '../../styles/dashboard.css';

const ManualExpositor = () => {
  // Estados para dados
  const [materiais, setMateriais] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [eventoSelecionado, setEventoSelecionado] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState({});
  
  // Estados para UI
  const [expandedSections, setExpandedSections] = useState({});
  const [alert, setAlert] = useState({ show: false, variant: '', message: '' });
  
  // Estados para preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [currentMaterial, setCurrentMaterial] = useState(null);
  
  // Estados para fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Tipos de materiais com ícones e cores
  const tiposMateriais = {
    'manual': { 
      nome: 'Manual do Expositor', 
      icon: FaBook, 
      color: 'primary',
      description: 'Informações essenciais para expositores'
    },
    'regulamento': { 
      nome: 'Regulamento', 
      icon: FaFileContract, 
      color: 'danger',
      description: 'Normas e regras do evento'
    },
    'formulario': { 
      nome: 'Formulário', 
      icon: FaWpforms, 
      color: 'success',
      description: 'Documentos para preenchimento'
    },
    'checklist': { 
      nome: 'Checklist', 
      icon: FaClipboardList, 
      color: 'warning',
      description: 'Listas de verificação'
    },
    'outros': { 
      nome: 'Outros Documentos', 
      icon: FaFolderOpen, 
      color: 'info',
      description: 'Documentos diversos'
    }
  };

  // Função para mostrar alertas
  const showAlert = (variant, message) => {
    setAlert({
      show: true,
      variant,
      message
    });
    
    setTimeout(() => {
      setAlert({ show: false, variant: '', message: '' });
    }, 5000);
  };

  // Função para entrar em tela cheia
  const enterFullscreen = () => {
    const element = document.documentElement;
    
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.mozRequestFullScreen) { // Firefox
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) { // Chrome, Safari e Opera
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) { // IE/Edge
      element.msRequestFullscreen();
    }
  };

  // Função para sair da tela cheia
  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) { // Firefox
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) { // Chrome, Safari e Opera
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) { // IE/Edge
      document.msExitFullscreen();
    }
  };

  // Função para alternar tela cheia
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      enterFullscreen();
    } else {
      exitFullscreen();
    }
  };

  // Listener para mudanças no estado de fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
    };

    // Adicionar listeners para diferentes navegadores
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    // Cleanup
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Carregar eventos do Supabase
  const carregarEventos = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_eventos')
        .select('id, nome, status, data_inicio, data_fim, ativo')
        .eq('ativo', true)
        .in('status', ['ativo', 'pendente', 'em_andamento']) 
        .order('data_inicio', { ascending: false });

      if (error) throw error;
      
      setEventos(data || []);
      
      // Se há eventos e nenhum está selecionado, selecionar o primeiro
      if (data && data.length > 0 && !eventoSelecionado) {
        setEventoSelecionado(data[0].id.toString());
      }
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      showAlert('danger', 'Erro ao carregar eventos: ' + error.message);
    }
  };

  // Carregar materiais de apoio do Supabase
  const carregarMateriais = async (eventoId = null) => {
    setLoading(true);
    try {
      let query = supabase
        .from('caex_materiais_apoio')
        .select(`
          id,
          titulo,
          descricao,
          tipo,
          arquivo_url,
          arquivo_nome,
          tamanho,
          formato,
          data_upload,
          evento_id,
          ativo,
          created_at,
          updated_at
        `)
        .eq('ativo', true)
        .order('tipo', { ascending: true })
        .order('titulo', { ascending: true });

      // Filtrar por evento se especificado
      if (eventoId) {
        query = query.eq('evento_id', eventoId);
      }

      const { data: materiaisData, error: materiaisError } = await query;

      if (materiaisError) throw materiaisError;

      // Buscar eventos relacionados separadamente
      const eventosIds = [...new Set(materiaisData.map(m => m.evento_id))];
      const { data: eventosData, error: eventosError } = await supabase
        .from('caex_eventos')
        .select('id, nome, status, data_inicio, data_fim')
        .in('id', eventosIds);

      if (eventosError) {
        console.warn('Erro ao carregar eventos:', eventosError);
      }

      // Criar mapa de eventos
      const eventosMap = {};
      if (eventosData) {
        eventosData.forEach(evento => {
          eventosMap[evento.id] = evento;
        });
      }

      // Processar os dados para o formato esperado pelo componente
      const materiaisFormatados = materiaisData.map(material => ({
        ...material,
        evento_nome: eventosMap[material.evento_id]?.nome || 'Evento não encontrado',
        evento_status: eventosMap[material.evento_id]?.status || 'indefinido',
        data_upload: new Date(material.data_upload).toLocaleDateString('pt-BR'),
        tipo_info: tiposMateriais[material.tipo] || tiposMateriais['outros']
      }));

      // Agrupar materiais por tipo
      const materiaisAgrupados = materiaisFormatados.reduce((acc, material) => {
        if (!acc[material.tipo]) {
          acc[material.tipo] = [];
        }
        acc[material.tipo].push(material);
        return acc;
      }, {});

      setMateriais(materiaisAgrupados);

      // Expandir automaticamente a primeira seção se houver materiais
      if (Object.keys(materiaisAgrupados).length > 0) {
        const primeiroTipo = Object.keys(materiaisAgrupados)[0];
        setExpandedSections(prev => ({
          ...prev,
          [primeiroTipo]: true
        }));
      }

    } catch (error) {
      console.error('Erro ao carregar materiais:', error);
      showAlert('danger', 'Erro ao carregar materiais: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle para expandir/recolher seções
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Função para download de arquivo (download direto)
  const handleDownload = async (material) => {
    setDownloadLoading(prev => ({ ...prev, [material.id]: true }));
    
    try {
      // Fazer fetch do arquivo
      const response = await fetch(material.arquivo_url);
      
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }
      
      // Converter para blob
      const blob = await response.blob();
      
      // Criar URL temporária
      const url = window.URL.createObjectURL(blob);
      
      // Criar elemento de download
      const link = document.createElement('a');
      link.href = url;
      link.download = material.arquivo_nome || `${material.titulo}.${material.formato.toLowerCase()}`;
      
      // Executar download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL temporária
      window.URL.revokeObjectURL(url);
      
      showAlert('success', 'Download concluído com sucesso!');
      
    } catch (error) {
      console.error('Erro no download:', error);
      showAlert('danger', 'Erro ao fazer download: ' + error.message);
    } finally {
      setDownloadLoading(prev => ({ ...prev, [material.id]: false }));
    }
  };

  // Função para download direto do modal (sem abrir nova aba)
  const handleModalDownload = async () => {
    if (!currentMaterial) return;
    
    setDownloadLoading(prev => ({ ...prev, ['modal']: true }));
    
    try {
      // Fazer fetch do arquivo
      const response = await fetch(currentMaterial.arquivo_url);
      
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }
      
      // Converter para blob
      const blob = await response.blob();
      
      // Criar URL temporária
      const url = window.URL.createObjectURL(blob);
      
      // Criar elemento de download
      const link = document.createElement('a');
      link.href = url;
      link.download = currentMaterial.arquivo_nome || `${currentMaterial.titulo}.${currentMaterial.formato.toLowerCase()}`;
      
      // Executar download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL temporária
      window.URL.revokeObjectURL(url);
      
      showAlert('success', 'Download concluído com sucesso!');
      
    } catch (error) {
      console.error('Erro no download:', error);
      showAlert('danger', 'Erro ao fazer download: ' + error.message);
    } finally {
      setDownloadLoading(prev => ({ ...prev, ['modal']: false }));
    }
  };

  // Função para visualizar arquivo
  const handlePreview = (material) => {
    setPreviewUrl(material.arquivo_url);
    setPreviewTitle(material.titulo);
    setCurrentMaterial(material);
    setShowPreview(true);
  };

  // Função para verificar se o arquivo é uma imagem
  const isImageFile = (url, formato) => {
    if (!url) return false;
    const imageFormats = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'];
    return imageFormats.includes(formato?.toUpperCase()) || 
           /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  };

  // Função para renderizar preview do arquivo
  const renderFilePreview = (arquivo_url, formato, arquivo_nome) => {
    if (!arquivo_url) {
      return (
        <div className="text-center py-3 bg-light rounded">
          <FaFileAlt size={32} className="text-muted mb-2" />
          <p className="text-muted small">Nenhum arquivo disponível</p>
        </div>
      );
    }

    if (isImageFile(arquivo_url, formato)) {
      return (
        <div className="position-relative">
          <img 
            src={arquivo_url} 
            alt={arquivo_nome || 'Arquivo'}
            className="img-fluid rounded"
            style={{ 
              maxHeight: '200px', 
              width: '100%', 
              objectFit: 'contain',
              backgroundColor: '#f8f9fa'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div 
            className="text-center py-3 bg-light rounded" 
            style={{ display: 'none' }}
          >
            <FaFileAlt size={32} className="text-muted mb-2" />
            <p className="text-muted small">Erro ao carregar imagem</p>
          </div>
        </div>
      );
    } else {
      // Para PDFs e outros arquivos
      return (
        <div className="text-center py-3 bg-light rounded">
          <FaFilePdf size={32} className="text-primary mb-2" />
          <p className="mb-1 small"><strong>{arquivo_nome}</strong></p>
          <p className="text-muted small">Arquivo {formato} - Clique para visualizar</p>
        </div>
      );
    }
  };

  // Função para renderizar conteúdo de um material
  const renderMaterialContent = (material) => {
    return (
      <div className="material-content">
        <Row>
          <Col md={4}>
            <div 
              className="file-preview-container cursor-pointer"
              onClick={() => handlePreview(material)}
              style={{ cursor: 'pointer' }}
            >
              {renderFilePreview(material.arquivo_url, material.formato, material.arquivo_nome)}
            </div>
          </Col>
          <Col md={8}>
            <div className="material-info">
              <h6 className="mb-2">{material.titulo}</h6>
              {material.descricao && (
                <p className="text-muted small mb-3">{material.descricao}</p>
              )}
              
              <div className="material-meta mb-3">
                <Row className="g-2">
                  <Col sm={6}>
                    <small className="text-muted">
                      <strong>Tamanho:</strong> {material.tamanho}
                    </small>
                  </Col>
                  <Col sm={6}>
                    <small className="text-muted">
                      <strong>Formato:</strong> {material.formato}
                    </small>
                  </Col>
                  <Col sm={6}>
                    <small className="text-muted">
                      <strong>Atualizado:</strong> {material.data_upload}
                    </small>
                  </Col>
                  <Col sm={6}>
                    <small className="text-muted">
                      <strong>Evento:</strong> {material.evento_nome}
                    </small>
                  </Col>
                </Row>
              </div>
              
              <div className="material-actions">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  className="me-2"
                  onClick={() => handlePreview(material)}
                >
                  <FaEye className="me-1" /> Visualizar
                </Button>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => handleDownload(material)}
                  disabled={downloadLoading[material.id]}
                >
                  {downloadLoading[material.id] ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      Baixando...
                    </>
                  ) : (
                    <>
                      <FaDownload className="me-1" /> Baixar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  // Carregar dados quando o componente monta
  useEffect(() => {
    carregarEventos();
  }, []);

  // Carregar materiais quando o evento selecionado muda
  useEffect(() => {
    if (eventoSelecionado) {
      carregarMateriais(eventoSelecionado);
    }
  }, [eventoSelecionado]);

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaBook className="dashboard-title-icon" />
              Manual do Expositor
            </h1>
            <p className="dashboard-subtitle">
              Informações e orientações essenciais para expositores
            </p>
          </div>
        </div>
        
        {/* Alerta de sucesso/erro */}
        {alert.show && (
          <Alert 
            variant={alert.variant} 
            onClose={() => setAlert({ show: false, variant: '', message: '' })} 
            dismissible
            className="mb-4"
          >
            {alert.message}
          </Alert>
        )}
        
        {/* Filtro por Evento */}
        <Card className="modern-table-card mb-4">
          <Card.Header className="modern-card-header">
            <h5 className="m-0 d-flex align-items-center gap-2">
              <FaFilter /> Filtrar por Evento
            </h5>
          </Card.Header>
          <Card.Body className="modern-card-body">
            <Row className="align-items-center">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Selecione o Evento</Form.Label>
                  <Form.Select 
                    value={eventoSelecionado} 
                    onChange={(e) => setEventoSelecionado(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Todos os eventos</option>
                    {eventos.map(evento => (
                      <option key={evento.id} value={evento.id}>
                        {evento.nome} 
                        {evento.status && ` (${evento.status})`}
                        {evento.data_inicio && ` - ${new Date(evento.data_inicio).toLocaleDateString('pt-BR')}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <div className="mt-3 mt-md-0">
                  {eventoSelecionado && (
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg="info">
                        {Object.keys(materiais).reduce((total, tipo) => total + materiais[tipo].length, 0)} materiais encontrados
                      </Badge>
                      {loading && (
                        <Spinner size="sm" variant="primary" />
                      )}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Loading Spinner */}
        {loading && (
          <div className="text-center mb-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Carregando materiais...</p>
          </div>
        )}

        {/* Conteúdo dos Materiais */}
        {!loading && eventoSelecionado && (
          <>
            <div className="dashboard-section-header">
              <h2 className="dashboard-section-title">
                <FaBookOpen className="dashboard-section-icon" />
                Materiais de Apoio
              </h2>
              <p className="dashboard-section-subtitle">
                Consulte as informações e documentos do evento selecionado
              </p>
            </div>

            <Card className="modern-table-card mb-4">
              <Card.Body className="modern-card-body">
                {Object.keys(materiais).length > 0 ? (
                  <Accordion>
                    {Object.entries(materiais).map(([tipo, materiaisTipo], index) => {
                      const tipoInfo = tiposMateriais[tipo] || tiposMateriais['outros'];
                      const IconComponent = tipoInfo.icon;
                      
                      return (
                        <Accordion.Item eventKey={index.toString()} key={tipo}>
                          <Accordion.Header>
                            <div className="d-flex align-items-center gap-2 w-100">
                              <IconComponent className={`text-${tipoInfo.color}`} /> 
                              <span className="flex-grow-1">{tipoInfo.nome}</span>
                              <Badge bg={tipoInfo.color} className="me-2">
                                {materiaisTipo.length}
                              </Badge>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <p className="text-muted small mb-3">{tipoInfo.description}</p>
                            
                            {materiaisTipo.map((material, materialIndex) => (
                              <div key={material.id}>
                                {renderMaterialContent(material)}
                                {materialIndex < materiaisTipo.length - 1 && <hr className="my-4" />}
                              </div>
                            ))}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                ) : (
                  <div className="text-center py-5">
                    <FaInfoCircle size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">Nenhum material encontrado</h5>
                    <p className="text-muted">
                      {eventoSelecionado 
                        ? 'Não há materiais de apoio cadastrados para o evento selecionado.'
                        : 'Selecione um evento para visualizar os materiais disponíveis.'
                      }
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}

        {/* Mensagem quando nenhum evento está selecionado */}
        {!loading && !eventoSelecionado && (
          <Card className="modern-table-card mb-4">
            <Card.Body className="modern-card-body text-center py-5">
              <FaCalendarAlt size={48} className="text-muted mb-3" />
              <h5 className="text-muted">Selecione um Evento</h5>
              <p className="text-muted">
                Escolha um evento no filtro acima para visualizar os materiais de apoio disponíveis.
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Informações Adicionais */}
        <Card className="modern-table-card mb-4">
          <Card.Header className="modern-card-header">
            <h5 className="m-0 d-flex align-items-center gap-2">
              <FaInfoCircle /> Informações Importantes
            </h5>
          </Card.Header>
          <Card.Body className="modern-card-body">
            <div className="alert alert-info">
              <strong>Dica:</strong> Clique nas imagens dos documentos para visualizá-los em tela cheia antes de fazer o download.
            </div>
            
            <Row>
              <Col md={6}>
                <h6>Como usar este manual:</h6>
                <ul className="small text-muted">
                  <li>Selecione o evento desejado no filtro acima</li>
                  <li>Navegue pelas categorias de documentos</li>
                  <li>Clique em "Visualizar" para ver o conteúdo</li>
                  <li>Use "Baixar" para salvar os arquivos localmente</li>
                  <li>Use o botão de tela cheia para melhor visualização</li>
                </ul>
              </Col>
              <Col md={6}>
                <h6>Suporte:</h6>
                <p className="small text-muted">
                  Em caso de dúvidas, entre em contato com nossa equipe através do e-mail 
                  <strong> caex@mmeventoshd.com.br</strong> ou pelo telefone <strong>(19) 99636-6402</strong>.
                </p>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
      
      {/* Modal de preview do arquivo com botão de tela cheia no canto superior direito */}
      <Modal 
        show={showPreview} 
        onHide={() => setShowPreview(false)} 
        size="xl"
        className={isFullscreen ? 'fullscreen-modal' : ''}
      >
        <Modal.Header closeButton className="position-relative">
          <Modal.Title className="d-flex align-items-center gap-2">
            <span>{previewTitle}</span>
            {isFullscreen && (
              <Badge bg="success" className="ms-2">
                <FaExpand className="me-1" />
                Tela Cheia
              </Badge>
            )}
          </Modal.Title>
          
          {/* Botão de tela cheia no canto superior direito */}
          <Button 
            variant={isFullscreen ? "warning" : "info"} 
            size="sm"
            onClick={toggleFullscreen}
            className="position-absolute"
            style={{ 
              top: '10px', 
              right: '50px', // Posicionado antes do botão de fechar
              zIndex: 1050 
            }}
            title={isFullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}
          >
            {isFullscreen ? (
              <FaCompress />
            ) : (
              <FaExpand />
            )}
          </Button>
        </Modal.Header>
        
        <Modal.Body className="p-0">
          <div className={`ratio ${isFullscreen ? 'ratio-21x9' : 'ratio-16x9'}`}>
            <iframe 
              src={previewUrl} 
              title="Visualização do arquivo"
              allowFullScreen
              style={{ border: 'none' }}
            ></iframe>
          </div>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowPreview(false)}
            className="me-2"
          >
            Fechar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleModalDownload}
            disabled={downloadLoading['modal']}
          >
            {downloadLoading['modal'] ? (
              <>
                <Spinner size="sm" className="me-2" />
                Baixando...
              </>
            ) : (
              <>
                <FaDownload className="me-2" /> Baixar Arquivo
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CSS adicional para tela cheia */}
      <style jsx>{`
        .fullscreen-modal .modal-dialog {
          max-width: 100vw !important;
          margin: 0 !important;
          height: 100vh !important;
        }
        
        .fullscreen-modal .modal-content {
          height: 100vh !important;
          border-radius: 0 !important;
          border: none !important;
        }
        
        .fullscreen-modal .modal-body {
          flex: 1 !important;
          display: flex !important;
          flex-direction: column !important;
        }
        
        .fullscreen-modal .ratio {
          flex: 1 !important;
          height: auto !important;
        }
        
        .fullscreen-modal .ratio iframe {
          height: 100% !important;
        }
        
        /* Estilo para o botão de tela cheia no canto superior direito */
        .modal-header .position-absolute {
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .modal-header .position-absolute:hover {
          transform: scale(1.05);
          transition: transform 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default ManualExpositor;